// src/components/pv/InverterCurveFitDialog.jsx
//
// Build a detailed (Sandia) inverter model from datasheet efficiency curves.
//
// Why this exists
// ---------------
// PVWatts already varies efficiency with load, using a generic curve. Fitting
// Sandia describes the supplied device-specific curves and DC-voltage dependence.
// This does not change the fixed-efficiency DC-coupled dispatch approximation.
// The backend has always
// been able to fit it (inverter_from_datasheet -> pvlib.inverter.fit_sandia);
// there was simply no way to enter the data.
//
// What the user enters is what a datasheet actually prints: efficiency against
// load, at low / nominal / high MPP voltage. From a point (load f, efficiency n)
// on the curve for voltage V we derive Pac = f x Paco and Pdc = Pac / n, which
// is exactly the grid pvlib wants — so no lab test report is needed.
//
// All three voltage levels are mandatory. C1, C2 and C3 ARE the voltage
// dependence, so two curves cannot constrain them; pvlib does not degrade, it
// raises. The dialog says so rather than letting the user discover it as a 422.
import { useEffect, useMemo, useState } from "react";
import { useTranslateText } from "../../context/LanguageContext";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
// ComposedChart, not LineChart: recharts only renders Scatter inside a
// ComposedChart (or ScatterChart), so entered points and fitted lines can only
// share one plot here. Same component PvGenerationChart already uses.
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { previewInverterFit } from "../../services/api";
import InverterCurveDigitizer from "./InverterCurveDigitizer";

const LEVELS = [
  { key: "Vmin", label: "Vmin (low MPP voltage)" },
  { key: "Vnom", label: "Vnom (nominal)" },
  { key: "Vmax", label: "Vmax (high MPP voltage)" },
];

// The CEC test protocol points. Pre-seeded because datasheet curves are usually
// read off at roughly these loads, so the user only fills in efficiency.
const DEFAULT_LOADS = [10, 20, 30, 50, 75, 100];

const LEVEL_COLOR = { Vmin: "#1f77b4", Vnom: "#2ca02c", Vmax: "#d62728" };

export const INVERTER_FIT_HELP = "Without custom curves, PVWatts already varies inverter efficiency with load using a generic curve. Fitting Sandia to manufacturer curves at three DC voltages can represent the specific inverter's part-load efficiency and voltage dependence. Accuracy depends on the source data and fit quality; it is not guaranteed by the model name.";
export const INVERTER_FIT_SCOPE = "This fit applies to PV production when Sandia is selected (directly or automatically). The current DC-coupled battery dispatch still uses a fixed inverter efficiency; fitting curves here does not change that approximation.";

const blankCurves = () =>
  LEVELS.map(({ key }) => ({
    level: key,
    dc_voltage: "",
    points: DEFAULT_LOADS.map((load) => ({ load, efficiency: "" })),
  }));

const num = (v) => {
  if (v == null || (typeof v === "string" && !v.trim())) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim()) return v.split(",").map((s) => s.trim());
  return [];
};

/**
 * Rebuild editable curves from the four stored measurement arrays.
 *
 * The catalogue keeps cec_ac_power / cec_dc_power / cec_dc_voltage /
 * cec_dc_voltagelevel, which is what pvlib fits from. Those are absolute powers,
 * so invert the conversion the dialog applied on save:
 *     load %      = Pac / Paco * 100
 *     efficiency  = Pac / Pdc * 100
 * Returns null when no usable stored curves exist, so the caller can fall back
 * to blank tables rather than showing a half-populated form.
 */
export function curvesFromCharacteristics(chars) {
  if (!chars) return null;
  const ac = asArray(chars.cec_ac_power).map(Number);
  const dc = asArray(chars.cec_dc_power).map(Number);
  const v = asArray(chars.cec_dc_voltage).map(Number);
  const lvl = asArray(chars.cec_dc_voltagelevel).map((s) => String(s));
  const paco = num(chars.Paco);
  const n = Math.min(ac.length, dc.length, v.length, lvl.length);
  if (!n || !paco) return null;

  const byLevel = new Map();
  for (let i = 0; i < n; i += 1) {
    if (![ac[i], dc[i], v[i]].every(Number.isFinite) || !dc[i]) continue;
    const key = LEVELS.find((l) => l.key.toLowerCase() === lvl[i].toLowerCase())?.key;
    if (!key) continue;
    if (!byLevel.has(key)) byLevel.set(key, { level: key, dc_voltage: v[i], points: [] });
    byLevel.get(key).points.push({
      load: Number(((ac[i] / paco) * 100).toFixed(3)),
      efficiency: Number(((ac[i] / dc[i]) * 100).toFixed(3)),
    });
  }
  if (!byLevel.size) return null;
  // Keep Vmin/Vnom/Vmax order and leave untouched levels blank.
  return LEVELS.map(({ key }) => {
    const found = byLevel.get(key);
    if (!found) return { level: key, dc_voltage: "", points: DEFAULT_LOADS.map((load) => ({ load, efficiency: "" })) };
    found.points.sort((a, b) => a.load - b.load);
    return found;
  });
}

/**
 * Rebuild the FIT RESULT that is already stored, so opening the dialog shows
 * the model the engine actually runs on instead of an empty frame waiting for
 * a re-fit. The curves are evaluated with the stored coefficients through the
 * same Sandia expression pvlib uses, so what is drawn is the stored model, not
 * a fresh fit that might differ from it.
 *
 *   A = Pdco (1 + C1 (V - Vdco)),  B = Pso (1 + C2 (V - Vdco))
 *   C = C0   (1 + C3 (V - Vdco))
 *   Pac = [Paco/(A-B) - C (A-B)] (Pdc - B) + C (Pdc - B)^2
 *
 * Returns null when the entry has no fitted coefficients, so a never-fitted
 * inverter still opens with empty tables and no misleading chart.
 */
export function resultFromCharacteristics(chars) {
  if (!chars) return null;
  const coefficients = {};
  for (const k of ["Pdco", "Vdco", "Pso", "C0", "C1", "C2", "C3"]) {
    const v = num(chars[k]);
    if (v == null) return null;
    coefficients[k] = v;
  }
  const paco = num(chars.Paco);
  if (!paco) return null;

  const ac = asArray(chars.cec_ac_power).map(Number);
  const dc = asArray(chars.cec_dc_power).map(Number);
  const vv = asArray(chars.cec_dc_voltage).map(Number);
  const lvl = asArray(chars.cec_dc_voltagelevel).map((s) => String(s));
  const n = Math.min(ac.length, dc.length, vv.length, lvl.length);
  if (!n) return null;

  const comparison = [];
  let maxError = 0;
  for (let i = 0; i < n; i += 1) {
    if (![ac[i], dc[i], vv[i]].every(Number.isFinite) || !dc[i]) continue;
    const level = LEVELS.find((l) => l.key.toLowerCase() === lvl[i].toLowerCase())?.key;
    if (!level) continue;
    const dv = vv[i] - coefficients.Vdco;
    const A = coefficients.Pdco * (1 + coefficients.C1 * dv);
    const B = coefficients.Pso * (1 + coefficients.C2 * dv);
    const C = coefficients.C0 * (1 + coefficients.C3 * dv);
    const acFit = (paco / (A - B) - C * (A - B)) * (dc[i] - B) + C * (dc[i] - B) ** 2;
    const entered = (ac[i] / dc[i]) * 100;
    const fitted = (acFit / dc[i]) * 100;
    if (Number.isFinite(entered) && Number.isFinite(fitted)) {
      maxError = Math.max(maxError, Math.abs(fitted - entered));
      comparison.push({
        load_pct: (ac[i] / paco) * 100,
        level,
        entered_efficiency_pct: entered,
        fitted_efficiency_pct: fitted,
      });
    }
  }
  if (!comparison.length) return null;
  return {
    coefficients,
    comparison,
    max_efficiency_error_pct: maxError,
    points_used: comparison.length,
    warnings: [],
    fromStored: true,
  };
}

const TEMPLATE_CSV = [
  "level,dc_voltage,load_pct,efficiency_pct",
  "# One row per measured point. level must be Vmin, Vnom or Vmax.",
  "# dc_voltage in volts; load_pct as % of rated AC power; efficiency_pct in %.",
  "# All three levels are required: the C1-C3 coefficients describe how",
  "# efficiency varies with DC voltage and cannot be fitted from fewer.",
  "Vmin,420,10,93.8",
  "Vmin,420,20,96.1",
  "Vmin,420,30,96.9",
  "Vmin,420,50,97.4",
  "Vmin,420,75,97.5",
  "Vmin,420,100,97.2",
  "Vnom,600,10,94.6",
  "Vnom,600,20,96.8",
  "Vnom,600,30,97.5",
  "Vnom,600,50,98.1",
  "Vnom,600,75,98.2",
  "Vnom,600,100,97.9",
  "Vmax,800,10,94.1",
  "Vmax,800,20,96.4",
  "Vmax,800,30,97.1",
  "Vmax,800,50,97.7",
  "Vmax,800,75,97.8",
  "Vmax,800,100,97.4",
].join("\n");

/**
 * Parse an uploaded CSV into curves.
 *
 * Accepts the template's load/efficiency form and also the raw CEC grid
 * (ac_power/dc_power), since a test report gives absolute powers rather than
 * percentages. Header names are matched loosely so a hand-edited file still works.
 */
export function parseCurvesCsv(text, paco) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("#"));
  if (lines.length < 2) throw new Error("The file has no data rows.");

  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const col = (...names) => header.findIndex((h) => names.includes(h));
  const iLvl = col("level", "voltage_level", "dc_voltage_level");
  const iV = col("dc_voltage", "voltage", "vdc");
  const iLoad = col("load_pct", "load", "load%", "loadpct");
  const iEff = col("efficiency_pct", "efficiency", "eff", "eff_pct");
  const iAc = col("ac_power", "pac", "p_ac");
  const iDc = col("dc_power", "pdc", "p_dc");

  if (iLvl < 0 || iV < 0) throw new Error("Missing a 'level' or 'dc_voltage' column.");
  const rawMode = iAc >= 0 && iDc >= 0;
  if (!rawMode && (iLoad < 0 || iEff < 0)) {
    throw new Error(
      "Provide either load_pct + efficiency_pct, or ac_power + dc_power columns."
    );
  }
  if (rawMode && !num(paco)) {
    throw new Error("Enter the rated AC power (Paco) before uploading a raw power grid.");
  }

  const byLevel = new Map();
  lines.slice(1).forEach((line, idx) => {
    const cells = line.split(",").map((c) => c.trim());
    const key = LEVELS.find(
      (l) => l.key.toLowerCase() === String(cells[iLvl] || "").toLowerCase()
    )?.key;
    if (!key) throw new Error(`Row ${idx + 2}: unknown level "${cells[iLvl]}".`);
    const v = Number(cells[iV]);
    if (!Number.isFinite(v) || v <= 0) throw new Error(`Row ${idx + 2}: bad dc_voltage.`);

    let load;
    let eff;
    if (rawMode) {
      const pac = Number(cells[iAc]);
      const pdc = Number(cells[iDc]);
      if (!Number.isFinite(pac) || !Number.isFinite(pdc) || pdc <= 0) {
        throw new Error(`Row ${idx + 2}: bad ac_power/dc_power.`);
      }
      load = (pac / num(paco)) * 100;
      eff = (pac / pdc) * 100;
    } else {
      load = Number(cells[iLoad]);
      eff = Number(cells[iEff]);
      if (!Number.isFinite(load) || !Number.isFinite(eff)) {
        throw new Error(`Row ${idx + 2}: bad load_pct/efficiency_pct.`);
      }
    }
    if (!byLevel.has(key)) byLevel.set(key, { level: key, dc_voltage: v, points: [] });
    byLevel.get(key).points.push({
      load: Number(load.toFixed(3)),
      efficiency: Number(eff.toFixed(3)),
    });
  });

  return LEVELS.map(({ key }) => {
    const found = byLevel.get(key);
    if (!found) {
      return { level: key, dc_voltage: "", points: DEFAULT_LOADS.map((load) => ({ load, efficiency: "" })) };
    }
    found.points.sort((a, b) => a.load - b.load);
    return found;
  });
}

/**
 * Compact tooltip: one row per voltage level, entered against fitted.
 *
 * The default tooltip listed every series it found, so the shared `load` key was
 * repeated once per level ("load : 75.00%" three times) and the entered/fitted
 * pairs were interleaved rather than sitting side by side.
 */
function FitTooltip({ active, payload, label }) {
  const translateText = useTranslateText();
  if (!active || !payload?.length) return null;
  const at = (k) => payload.find((p) => p.dataKey === k)?.value;
  const rows = LEVELS.map(({ key }) => ({
    key,
    entered: at(`entered_${key}`),
    fitted: at(`fitted_${key}`),
  })).filter((r) => r.entered != null || r.fitted != null);
  if (!rows.length) return null;

  const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        boxShadow: 3,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
        {translateText(`Load ${Number(label).toFixed(0)}% of rating`)}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto auto auto",
          columnGap: 1.25,
          alignItems: "center",
        }}
      >
        <span />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {translateText("entered")}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {translateText("fitted")}
        </Typography>
        {rows.map((r) => [
          <Typography
            key={`${r.key}-l`}
            variant="caption"
            sx={{ fontWeight: 700, color: LEVEL_COLOR[r.key] }}
          >
            {r.key}
          </Typography>,
          <Typography key={`${r.key}-e`} variant="caption">
            {pct(r.entered)}
          </Typography>,
          <Typography key={`${r.key}-f`} variant="caption">
            {pct(r.fitted)}
          </Typography>,
        ])}
      </Box>
    </Box>
  );
}

export default function InverterCurveFitDialog({
  open,
  onClose,
  onApply,
  inverterName,
  initialPaco = "",
  initialPnt = "",
  sharedPowerInputs = false,
  characteristics = null,
  // Set for an inverter that already exists in the catalogue. The engine looks
  // equipment up live by name at simulation time and nothing snapshots it into
  // the run record, so overwriting a stored fit would retroactively change every
  // project using that inverter. In this mode the curves stay fully viewable and
  // re-fittable, but the only way out is a NEW catalogue entry.
  existingName = "",
}) {
  const readOnly = Boolean(existingName);
  const translateText = useTranslateText();
  const [newName, setNewName] = useState("");
  const [paco, setPaco] = useState(initialPaco);
  const [pnt, setPnt] = useState(initialPnt);
  const [curves, setCurves] = useState(blankCurves);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [loadedFromStore, setLoadedFromStore] = useState(false);
  const [digitizerOpen, setDigitizerOpen] = useState(false);

  useEffect(() => {
    if (!open) setDigitizerOpen(false);
  }, [open]);

  // Load whatever is already stored each time the dialog opens. Without this the
  // dialog was write-only: an inverter with a saved fit showed three empty
  // tables, and saving would have replaced its curves with whatever was retyped.
  useEffect(() => {
    if (!open) return;
    setError("");
    setPaco(initialPaco ?? "");
    setPnt(initialPnt ?? "");
    const stored = curvesFromCharacteristics(characteristics);
    setCurves(stored || blankCurves());
    setLoadedFromStore(Boolean(stored));
    // Show the model that is already stored straight away. Requiring a re-fit
    // just to see it left the dialog looking empty for an inverter that has a
    // perfectly good model, and invited a re-fit where none was wanted.
    setResult(resultFromCharacteristics(characteristics));
    setNewName(existingName ? `${existingName} (refit)` : "");
  }, [open, initialPaco, initialPnt, characteristics, existingName]);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inverter_efficiency_curves_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    setError("");
    setResult(null);
    try {
      const parsed = parseCurvesCsv(await file.text(), paco);
      setCurves(parsed);
      setLoadedFromStore(false);
    } catch (err) {
      setError(err?.message || "The file could not be read.");
    }
  };

  const useDigitizedCurves = (digitized) => {
    setCurves(digitized);
    setResult(null);
    setLoadedFromStore(false);
  };

  const setCurve = (level, patch) => {
    // A fitted model must always describe the values currently in the form.
    setResult(null);
    setCurves((prev) =>
      prev.map((c) => (c.level === level ? { ...c, ...patch } : c))
    );
  };

  const setPoint = (level, idx, patch) => {
    setResult(null);
    setCurves((prev) =>
      prev.map((c) =>
        c.level === level
          ? {
              ...c,
              points: c.points.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
            }
          : c
      )
    );
  };

  const addPoint = (level) => {
    setResult(null);
    setCurves((prev) =>
      prev.map((c) =>
        c.level === level
          ? { ...c, points: [...c.points, { load: "", efficiency: "" }] }
          : c
      )
    );
  };

  const removePoint = (level, idx) => {
    setResult(null);
    setCurves((prev) =>
      prev.map((c) =>
        c.level === level
          ? { ...c, points: c.points.filter((_, i) => i !== idx) }
          : c
      )
    );
  };

  // Only complete points count. A half-typed row must not be sent as a
  // measurement, and must not make the Fit button look available.
  const cleaned = useMemo(
    () =>
      curves.map((c) => ({
        level: c.level,
        dc_voltage: num(c.dc_voltage),
        points: c.points
          .map((p) => ({ load: num(p.load), efficiency: num(p.efficiency) }))
          .filter((p) => p.load != null && p.efficiency != null),
      })),
    [curves]
  );

  const readiness = useMemo(() => {
    const problems = [];
    if (num(paco) == null || num(paco) <= 0) problems.push("Rated AC power (Paco)");
    if (num(pnt) == null || num(pnt) < 0) problems.push("Night tare (Pnt)");
    for (const c of cleaned) {
      if (c.dc_voltage == null || c.dc_voltage <= 0) problems.push(`${c.level} DC voltage`);
      if (c.points.length < 3) problems.push(`${c.level}: at least 3 points`);
      if (c.points.some((p) => p.load <= 0 || p.load > 100 || p.efficiency <= 0 || p.efficiency > 100)) {
        problems.push(`${c.level}: load and efficiency must be between 0 and 100%`);
      }
    }
    const v = Object.fromEntries(cleaned.map((c) => [c.level, c.dc_voltage]));
    const ordered =
      v.Vmin != null && v.Vnom != null && v.Vmax != null
        ? v.Vmin < v.Vnom && v.Vnom < v.Vmax
        : true;
    if (!ordered) problems.push("DC voltages must increase Vmin → Vnom → Vmax");
    return { ok: problems.length === 0, problems };
  }, [paco, pnt, cleaned]);

  const runFit = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const data = await previewInverterFit({
        paco: num(paco),
        pnt: num(pnt),
        // The form labels both columns as percent. Send unambiguous fractions
        // so a 1% chart point can never be read by the API as 100% load.
        curves: cleaned.map((curve) => ({
          ...curve,
          points: curve.points.map((point) => ({
            load: point.load / 100,
            efficiency: point.efficiency / 100,
          })),
        })),
      });
      setResult(data);
    } catch (err) {
      setError(err?.message || "The fit could not be computed.");
    } finally {
      setBusy(false);
    }
  };

  // Chart: entered points as scatter, fitted model as lines, grouped by level.
  const chartData = useMemo(() => {
    if (!result?.comparison?.length) return [];
    const byLoad = new Map();
    for (const row of result.comparison) {
      const key = Math.round(row.load_pct * 10) / 10;
      if (!byLoad.has(key)) byLoad.set(key, { load: key });
      const o = byLoad.get(key);
      o[`entered_${row.level}`] = row.entered_efficiency_pct;
      o[`fitted_${row.level}`] = row.fitted_efficiency_pct;
    }
    return [...byLoad.values()].sort((a, b) => a.load - b.load);
  }, [result]);

  // Pad the efficiency axis around the actual data. "auto" clamps hard to the
  // min and max, which pressed the converged high-load section against the top
  // of the frame; a little headroom below and above separates the three curves.
  const yDomain = useMemo(() => {
    const vals = chartData.flatMap((row) =>
      LEVELS.flatMap(({ key }) => [row[`entered_${key}`], row[`fitted_${key}`]])
    ).filter((v) => Number.isFinite(v));
    if (!vals.length) return ["auto", "auto"];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = Math.max(0.4, (hi - lo) * 0.12);
    return [Math.max(0, Math.floor((lo - pad) * 2) / 2), Math.min(100, Math.ceil((hi + pad) * 2) / 2)];
  }, [chartData]);

  const apply = () => {
    if (!result?.coefficients) return;
    // Hand back both the fitted coefficients and the raw arrays, so the caller
    // can persist the measurements alongside the fit and refit later.
    const grid = { ac: [], dc: [], v: [], lvl: [] };
    for (const c of cleaned) {
      for (const p of c.points) {
        const pac = p.load / 100 * num(paco);
        const eta = p.efficiency / 100;
        grid.ac.push(pac);
        grid.dc.push(pac / eta);
        grid.v.push(c.dc_voltage);
        grid.lvl.push(c.level);
      }
    }
    onApply?.({
      coefficients: result.coefficients,
      characteristics: {
        Paco: num(paco),
        Pnt: num(pnt),
        cec_ac_power: grid.ac,
        cec_dc_power: grid.dc,
        cec_dc_voltage: grid.v,
        cec_dc_voltagelevel: grid.lvl,
      },
      // Non-empty only in read-only mode: tells the caller to create a new
      // catalogue entry rather than update the one being viewed.
      saveAsName: readOnly ? newName.trim() : "",
    });
    onClose?.();
  };

  // In read-only mode a distinct, non-blank name is the whole safety mechanism.
  const nameProblem = !readOnly
    ? ""
    : !newName.trim()
      ? "Enter a name for the new inverter."
      : newName.trim() === existingName
        ? "The new name must differ from the inverter being viewed."
        : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {translateText("Detailed inverter model")}{inverterName ? ` – ${inverterName}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>{translateText("What this improves")}</AlertTitle>
          <Typography variant="body2">
            {translateText(INVERTER_FIT_HELP)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {translateText(INVERTER_FIT_SCOPE)}
          </Typography>
        </Alert>

        {readOnly && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              {existingName} is already in the catalogue
            </AlertTitle>
            <Typography variant="body2">
              Inspect and re-fit the curves here, then save the result as a{" "}
              <b>new</b> inverter. This one is left untouched, so projects already
              using it are unaffected.
            </Typography>
          </Alert>
        )}

        {loadedFromStore && !readOnly && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Loaded the curve points already stored for this inverter. Edit them
              and re-fit, or leave them as they are.
            </Typography>
          </Alert>
        )}

        {readOnly && (
          <TextField
            size="small"
            fullWidth
            label="Save as a new inverter named"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={Boolean(nameProblem)}
            helperText={nameProblem || "The original keeps its stored model."}
            sx={{ mb: 2 }}
          />
        )}

        {sharedPowerInputs ? <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2">{translateText("Power inputs from the inverter form")}: Paco = {paco || "—"} W · Pnt = {pnt === "" || pnt == null ? "—" : pnt} W</Typography>
          <Typography variant="caption" color="text.secondary">{translateText("Enter or change Paco and Pnt in the main inverter form. They are used here automatically.")}</Typography>
        </Box> : <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            label={translateText("Rated AC power, Paco (W)")}
            value={paco}
            onChange={(e) => { setPaco(e.target.value); setResult(null); }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <TextField
            size="small"
            label={translateText("Night tare, Pnt (W)")}
            value={pnt}
            onChange={(e) => { setPnt(e.target.value); setResult(null); }}
            helperText={translateText("If the datasheet gives only an upper bound (for example <5 W), enter a documented assumption.")}
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Stack>}

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 2, flexWrap: "wrap" }}
          useFlexGap
        >
          <Button size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
            Download template
          </Button>
          <Button size="small" component="label" startIcon={<UploadFileIcon />}>
            Upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={(e) => {
                uploadCsv(e.target.files?.[0]);
                e.target.value = "";   // let the same file be re-picked
              }}
            />
          </Button>
          <Button size="small" variant="outlined" onClick={() => setDigitizerOpen(true)}>
            {translateText("Capture points from chart image")}
          </Button>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Accepts load %/efficiency % or a raw ac_power/dc_power grid.
          </Typography>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          }}
        >
          {curves.map((curve) => (
            <Box
              key={curve.level}
              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.25 }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Chip
                  size="small"
                  label={curve.level}
                  sx={{
                    fontWeight: 700,
                    bgcolor: LEVEL_COLOR[curve.level],
                    color: "#fff",
                  }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {translateText(LEVELS.find((l) => l.key === curve.level)?.label)}
                </Typography>
              </Stack>

              <TextField
                size="small"
                fullWidth
                label={translateText("DC voltage (V)")}
                value={curve.dc_voltage}
                onChange={(e) => setCurve(curve.level, { dc_voltage: e.target.value })}
                sx={{ mb: 1 }}
              />

              <Table size="small" sx={{ "& td, & th": { px: 0.5, py: 0.25 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{translateText("Load %")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{translateText("Eff. %")}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {curve.points.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <TextField
                          variant="standard"
                          value={p.load}
                          onChange={(e) => setPoint(curve.level, i, { load: e.target.value })}
                          sx={{ width: 58 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="standard"
                          value={p.efficiency}
                          onChange={(e) =>
                            setPoint(curve.level, i, { efficiency: e.target.value })
                          }
                          sx={{ width: 62 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removePoint(curve.level, i)}
                          disabled={curve.points.length <= 3}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button size="small" startIcon={<AddIcon />} onClick={() => addPoint(curve.level)}>
                Add point
              </Button>
            </Box>
          ))}
        </Box>

        {!readiness.ok && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {translateText("Still needed:")} {readiness.problems.map(translateText).join("; ")}.
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {translateText(error)}
          </Alert>
        )}

        {result && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }} useFlexGap>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {result.fromStored ? "Stored model" : "Fit result"}
              </Typography>
              <Chip
                size="small"
                color={result.max_efficiency_error_pct <= 1 ? "success" : "warning"}
                variant="outlined"
                label={`max error ${result.max_efficiency_error_pct?.toFixed(2)}%`}
                sx={{ fontWeight: 700 }}
              />
              <Chip size="small" variant="outlined" label={`${result.points_used} points`} />
              {/* The efficiency the stored rating implies at nominal DC. It is
                  the figure the simulation converts with, and a value far from
                  the datasheet peak means the rating pair is wrong. */}
              {num(paco) > 0 && num(result.coefficients?.Pdco) > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={
                    num(paco) / num(result.coefficients.Pdco) >= 0.93 ? "default" : "error"
                  }
                  label={`nameplate Paco/Pdco ${(
                    (num(paco) / num(result.coefficients.Pdco)) * 100
                  ).toFixed(2)}%`}
                />
              )}
            </Stack>

            {/* Taller than the default: the three curves converge above ~50%
                load, so vertical room is what separates them. The y domain is
                also padded rather than "auto", which otherwise clamps to the
                data and squeezes everything into the top of the frame. */}
            <Box sx={{ height: 400, mb: 0.5 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 16, bottom: 24, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="load"
                    type="number"
                    domain={[0, 105]}
                    tickCount={8}
                    label={{ value: translateText("Load (% of rating)"), position: "insideBottom", offset: -14 }}
                  />
                  <YAxis
                    domain={yDomain}
                    tickCount={8}
                    tickFormatter={(v) => v.toFixed(1)}
                    label={{ value: translateText("Efficiency (%)"), angle: -90, position: "insideLeft" }}
                  />
                  <RTooltip content={<FitTooltip />} />
                  {/* Only the entered series appear in the legend. Six entries —
                        one entered plus one fitted per level — wrapped onto two
                        rows and collided with the plot; the caption below says
                        which is which instead. */}
                  <Legend
                    verticalAlign="bottom"
                    height={24}
                    payload={LEVELS.map(({ key }) => ({
                      value: key,
                      type: "circle",
                      color: LEVEL_COLOR[key],
                      id: key,
                    }))}
                  />
                  {LEVELS.map(({ key }) => (
                    <Line
                      key={`f-${key}`}
                      type="monotone"
                      dataKey={`fitted_${key}`}
                      name={`${key} fitted`}
                      stroke={LEVEL_COLOR[key]}
                      strokeWidth={1.8}
                      dot={false}
                      connectNulls={false}
                      legendType="none"
                      isAnimationActive={false}
                    />
                  ))}
                  {LEVELS.map(({ key }) => (
                    <Scatter
                      key={`e-${key}`}
                      dataKey={`entered_${key}`}
                      name={`${key} entered`}
                      fill={LEVEL_COLOR[key]}
                      legendType="none"
                      isAnimationActive={false}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", mb: 1, textAlign: "center" }}
            >
              Dots are the values you entered; lines are the fitted model.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gap: 0.5,
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))" },
                p: 1.25,
                borderRadius: 1,
                bgcolor: "action.hover",
              }}
            >
              {["Pdco", "Vdco", "Pso", "C0", "C1", "C2", "C3"].map((k) => (
                <Stack key={k} direction="row" spacing={0.75}>
                  <Typography variant="caption" data-no-translate sx={{ color: "text.secondary" }}>
                    {k}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {result.coefficients?.[k] == null
                      ? "—"
                      : Number(result.coefficients[k]).toPrecision(6)}
                  </Typography>
                </Stack>
              ))}
            </Box>

            {(result.warnings || []).length > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {result.warnings.map((w, i) => (
                  <Typography key={i} variant="caption" sx={{ display: "block" }}>
                    {translateText(w)}
                  </Typography>
                ))}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{translateText("Cancel")}</Button>
        <Tooltip title={readiness.ok ? "" : translateText("Complete the fields listed above first")}>
          <span>
            <Button
              onClick={runFit}
              disabled={!readiness.ok || busy}
              startIcon={busy ? <CircularProgress size={14} /> : null}
            >
              {translateText(busy ? "Fitting…" : "Fit model")}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          onClick={apply}
          disabled={!result?.coefficients || Boolean(nameProblem)}
        >
          {translateText(readOnly ? "Save as a new inverter" : "Use this model")}
        </Button>
      </DialogActions>
      <InverterCurveDigitizer
        open={digitizerOpen}
        onClose={() => setDigitizerOpen(false)}
        onUse={useDigitizedCurves}
        initialCurves={curves}
      />
    </Dialog>
  );
}
